import React, { useState } from "react";
import { Link } from "react-router-dom";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { toast } from "sonner";
import { ArrowLeft, MailQuestion, CheckCircle2 } from "lucide-react";
import api from "@/lib/api";

/**
 * ForgotPassword — public form that opens a password-reset ticket for a
 * Pythagore administrator to handle out-of-band (WhatsApp / phone).
 *
 * Backend POST /api/auth/forgot-password is rate-limited & always returns 202
 * to avoid account enumeration, so the success message we display here is
 * intentionally generic regardless of whether the email exists.
 */
export default function ForgotPassword() {
  const [email, setEmail] = useState("");
  const [reason, setReason] = useState("");
  const [loading, setLoading] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await api.post("/auth/forgot-password", { email: email.trim().toLowerCase(), reason: reason.trim() || undefined });
      setSubmitted(true);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Impossible d'envoyer la demande.");
    } finally {
      setLoading(false);
    }
  };

  if (submitted) {
    return (
      <div className="min-h-screen flex items-center justify-center px-4 bg-zinc-50/40">
        <div className="max-w-md w-full border border-zinc-200 bg-white p-8 rounded-sm" data-testid="forgot-password-confirmation">
          <div className="flex items-center gap-3 mb-3">
            <CheckCircle2 className="h-6 w-6 text-emerald-600" />
            <h1 className="text-xl font-semibold text-zinc-900">Demande enregistrée</h1>
          </div>
          <p className="text-sm text-zinc-700 leading-relaxed">
            Si un compte existe pour <span className="font-mono text-zinc-900">{email}</span>, un administrateur Pythagore vous contactera dans les <strong>24 heures</strong> via votre canal habituel (WhatsApp ou téléphone) pour vous transmettre un mot de passe temporaire sécurisé.
          </p>
          <p className="mt-3 text-[12px] text-zinc-500">
            À sa réception, connectez-vous puis modifiez-le immédiatement depuis votre profil.
          </p>
          <Link
            to="/login"
            className="mt-6 inline-flex items-center gap-2 text-sm text-[#D42B1E] hover:underline"
            data-testid="forgot-password-back-to-login"
          >
            <ArrowLeft className="h-4 w-4" /> Retour à la connexion
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex items-center justify-center px-4 bg-zinc-50/40" data-testid="forgot-password-page">
      <div className="max-w-md w-full">
        <Link to="/login" className="inline-flex items-center gap-2 text-xs text-zinc-500 hover:text-zinc-900 mb-4">
          <ArrowLeft className="h-3.5 w-3.5" /> Retour à la connexion
        </Link>
        <div className="border border-zinc-200 bg-white p-8 rounded-sm">
          <div className="flex items-center gap-3 mb-2">
            <span className="h-9 w-9 rounded-sm bg-[#D42B1E]/[0.08] text-[#D42B1E] flex items-center justify-center">
              <MailQuestion className="h-4 w-4" />
            </span>
            <h1 className="text-xl font-semibold text-zinc-900">Mot de passe oublié</h1>
          </div>
          <p className="text-sm text-zinc-600 leading-relaxed">
            Un administrateur Pythagore prendra contact pour vous transmettre un mot de passe temporaire sécurisé.
            Vous le modifierez ensuite depuis votre profil.
          </p>

          <form onSubmit={submit} className="mt-5 space-y-4" data-testid="forgot-password-form">
            <div>
              <Label className="text-xs font-semibold uppercase tracking-wide text-zinc-700">Email professionnel</Label>
              <Input
                type="email"
                required
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="prenom.nom@exemple.com"
                className="mt-1 rounded-sm"
                data-testid="forgot-password-email"
              />
            </div>
            <div>
              <Label className="text-xs font-semibold uppercase tracking-wide text-zinc-700">
                Raison <span className="text-zinc-400 font-normal normal-case tracking-normal">(optionnel)</span>
              </Label>
              <Textarea
                rows={3}
                maxLength={400}
                value={reason}
                onChange={(e) => setReason(e.target.value)}
                placeholder="Ex. Mot de passe oublié après congés, nouveau poste de travail…"
                className="mt-1 rounded-sm text-sm"
                data-testid="forgot-password-reason"
              />
              <p className="mt-1 text-[10px] text-zinc-500 text-right">{reason.length}/400</p>
            </div>
            <Button
              type="submit"
              disabled={loading}
              className="w-full bg-[#D42B1E] hover:bg-[#B32218] rounded-sm h-11"
              data-testid="forgot-password-submit"
            >
              {loading ? "Envoi…" : "Envoyer ma demande"}
            </Button>
          </form>
        </div>
      </div>
    </div>
  );
}
